import type { RailwayProjectSummary, RailwayRunner } from '@cli-types/next-adapter/railway.js'
import { MAX_PROJECT_NAME_ATTEMPTS } from '@cli-constants/limits.js'
import { outputTail } from '@cli-utils/process.js'
import { parseRailwayJson } from '@next-adapter/init/railway/runner/parse-railway-json.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import { isRailwayProjectNameConflict } from './is-railway-project-name-conflict.js'
import { parseProjectSummary } from './parse-project-summary.js'
import { railwayProjectNameIsTaken } from './railway-project-name-is-taken.js'
import { versionedRailwayProjectName } from './versioned-railway-project-name.js'

export async function createRailwayProject(
  runner: RailwayRunner,
  cwd: string,
  projectName: string,
  existingProjects: RailwayProjectSummary[],
  options: { workspace?: string; env?: NodeJS.ProcessEnv }
): Promise<RailwayProjectSummary> {
  const knownProjects = [...existingProjects]

  for (let attempt = 1; attempt <= MAX_PROJECT_NAME_ATTEMPTS; attempt++) {
    const candidate = versionedRailwayProjectName(projectName, attempt)
    if (railwayProjectNameIsTaken(knownProjects, candidate)) continue

    const args = ['init', '--name', candidate, '--json']
    if (options.workspace) args.push('--workspace', options.workspace)

    const result = await runRailway(runner, args, { cwd, mode: 'capture', env: options.env })
    const project = result.success
      ? parseProjectSummary(parseRailwayJson<unknown>(result.stdout))
      : undefined
    if (project) return project

    const detail = outputTail(`${result.stdout}\n${result.stderr}`)
    if (!detail || !isRailwayProjectNameConflict(detail)) {
      throw new Error(detail ?? `Could not create Railway project ${candidate}.`)
    }

    knownProjects.push({ id: candidate, name: candidate })
  }

  throw new Error(
    `Could not create a unique Railway project name after ${MAX_PROJECT_NAME_ATTEMPTS} attempts.`
  )
}
